import { agentLoginApi } from "../../../api/post/agentAuthApi"; // Import the agentLogin API call
import { loginSuccess, logout, resetAppState } from "../auth/authSlice"; // Import actions from authSlice
import { logoutAgent } from "../agent/agentSlice"; // Import logoutAgent action
import { logoutAgentApi } from "../../../api/post/agentLogoutApi";
import { setAuthToken } from "../../../api/config/auth";
import { fetchAgentInfo } from "../agent/agentThunks";
import { selectIsAuthenticated, selectAuthToken } from "./authSelectors";

/**
 * Helper function to clear persisted state from localStorage only
 * Now we handle Redux reset separately
 */
const clearLocalStorage = () => {
  // Keep the current token if needed for the login process
  const currentToken = localStorage.getItem("token");
  
  // Clear all localStorage items except what we want to keep
  localStorage.clear();
  
  // Return the token in case we need it
  return currentToken;
};

/**
 * Thunk to handle agent login
 * @param {Object} credentials - { email, password }
 */
export const loginUser = (credentials) => async (dispatch, getState) => {
  try {
    // Check if user is already authenticated before resetting state
    const isAuthenticated = selectIsAuthenticated(getState());
    
    // Only reset if not authenticated
    if (!isAuthenticated) {
      dispatch(resetAppState());
      clearLocalStorage();
    }
    
    const response = await agentLoginApi(credentials); // Call the agent login API
    if (response.data?.token) {
      dispatch(loginSuccess(response.data.token)); // Store the token in Redux
      setAuthToken(response.data.token); // Set the token for subsequent API calls
      // Call the fetchAgentInfo thunk to get agent info after login
      await dispatch(fetchAgentInfo());
    }
    return response.data;
  } catch (error) {
    console.error("Login failed:", error);
    throw error;
  }
};

/**
 * Thunk to handle agent login with token
 * @param {Object} token - { token }
 */
export const loginUserWithToken = (token) => async (dispatch, getState) => {
  try {
    if (token) {
      // Check if user is already authenticated with the same token
      const isAuthenticated = selectIsAuthenticated(getState());
      const currentToken = selectAuthToken(getState());
      
      // Only reset if not authenticated or token has changed
      if (!isAuthenticated || currentToken !== token) {
        dispatch(resetAppState());
      }
      
      dispatch(loginSuccess(token)); // Store the token in Redux
      setAuthToken(token); // Ensure token is set for API calls
      
      // Call the fetchAgentInfo thunk to get agent info after login
      await dispatch(fetchAgentInfo());
    }
  } catch (error) {
    console.error("Login with token failed:", error);
    throw error;
  }
};

/**
 * Thunk to handle full user logout
 */
export const logoutUser = () => async (dispatch) => {
  try {
    await logoutAgentApi(); // Call the logoutAgent API to log out the agent

    // Reset the entire Redux state
    dispatch(resetAppState());
    
    // No need to dispatch individual logout actions since the state is completely reset
    // But we keep these for specific handling if needed
    dispatch(logout()); // Clear auth token
    dispatch(logoutAgent()); // Clear agent info
  } catch (error) {
    console.error("Logout failed:", error);
    throw error;
  }
};
